import Vue from 'vue'
import * as types from '../mutation-types'
import judging from '@/api/judging'
import projects from '@/api/projects'
import users from '@/api/users'
import { fetchStatuses } from '@/mixins/fetch-status'
import { judgingConfig } from '@/mixins/judging-config'

const getDefaultState = () => {
  return {
    config: judgingConfig(),
    currentTab: 'Pending',
    teams: {},
    teamsLoadingStatus: fetchStatuses.FetchStatusIdle,
    teamsLoadingError: null,
    teamsSwitchStatus: fetchStatuses.FetchStatusIdle,
    teamsSwitchError: null,
    teamLeadSetStatus: fetchStatuses.FetchStatusIdle,
    teamLeadSetError: null,
    teamUserRemoveStatus: fetchStatuses.FetchStatusIdle,
    teamUserRemoveError: null,
    teamGroups: {},
    teamGroupsStatus: fetchStatuses.FetchStatusIdle,
    teamGroupsError: null,
    teamLockStatus: fetchStatuses.FetchStatusIdle,
    teamLockError: null,
    teamProjects: {},
    teamProjectsStatuses: {},
    teamProjectsErrors: {},
    teamProjectsTags: [],
    teamProjectsTagsStatus: fetchStatuses.FetchStatusIdle,
    teamProjectsTagsError: null,
    teamProjectsAssignStatus: fetchStatuses.FetchStatusIdle,
    teamProjectsAssignError: null,
    teamProjectsScheduleStatus: fetchStatuses.FetchStatusIdle,
    teamProjectsScheduleError: null,
    rubricDownloadStatus: 'idle',
    rubricDownloadError: null,
    rubricUploadStatus: 'idle',
    rubricUploadError: null,
    judgePairingError: null,
    judgePairingStatus: null,
    judgePairingScore: null
  }
}

const state = getDefaultState()

const getters = {
  getJudgingConfig: (state) => {
    return state.config
  },
  getCurrentTab: (state) => {
    return state.currentTab
  },
  getJudgingTeamGroups: (state) => {
    if (state.teamGroupsStatus === fetchStatuses.FetchStatusLoaded) {
      return Object.values(state.teamGroups)
    } else {
      return []
    }
  },
  getJudgingTeams: (state) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      return Object.values(state.teams)
    } else {
      return []
    }
  },
  getJudgingTeam: (state) => (teamId) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      return teamId in state.teams ? state.teams[teamId] : null
    }
  },
  getJudgingTeamStatus: (state) => {
    return state.teamsLoadingStatus
  },
  getJudgingTeamLockStatus: (state) => (teamId) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      if (state.teams[teamId] && state.teams[teamId].projectStatus) {
        // Bitwise operator to make sure project status contains 32 (Locked)
        // e.g. status = 33 means 1 (pending) & 33 (locked)
        // eslint-disable-next-line no-self-compare
        return (state.teams[teamId].projectStatus.status & 32) === 32
      }
    }
    return false
  },
  getJudgingTeamProjects: (state) => (teamId) => {
    if (state.teamProjectsStatuses[teamId] && state.teamProjectsStatuses[teamId] === fetchStatuses.FetchStatusLoaded) {
      return teamId in state.teamProjects ? state.teamProjects[teamId] : []
    }
    return []
  },
  getJudgingTeamProjectsStatus: (state) => (teamId) => {
    return teamId in state.teamProjectsStatuses ? state.teamProjectsStatuses[teamId] : fetchStatuses.FetchStatusIdle
  },
  getTeamProjectTags: (state) => {
    return state.teamProjectsTags
  },
  getCurrentTeamIDs: (state) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      return Object.keys(state.teams).map(m => parseInt(m))
    }
    return []
  },
  getTeamScheduleStatus: (state) => {
    return state.teamProjectsScheduleStatus
  },
  getTeamScheduleError: (state) => {
    return state.teamProjectsScheduleError
  },
  getUnassignedApprovedUsers: (state, getters, rootState) => {
    const assignedUserIDs = getters.getJudgingTeams.reduce((acc, current) => {
      return acc.concat(current.memberships.map(m => m.user.id))
    }, [])

    return Object.values(rootState.project.approvedProjects).map(m => m.user).filter(m => !assignedUserIDs.includes(m.id)).sort((a, b) => a.firstName > b.firstName ? 1 : -1)
  },
  getJudgePairingScore: (state) => {
    return state.judgePairingScore
  },
  getJudgePairingStatus: (state) => {
    return state.judgePairingStatus
  }
}

// actions
const actions = {
  switchTab ({ commit }, { tabName }) {
    commit(types.JUDGING_TAB_SWITCHED, tabName)
  },
  setJudgingTeamLockStatus ({ commit, getters }, { teamID }) {
    const currentLockStatus = getters.getJudgingTeamLockStatus(teamID)
    commit(types.JUDGING_SET_TEAM_LOCK_LOADING)
    if (!currentLockStatus) {
      projects.addProjectStatus(teamID, 'Locked').then((response) => {
        commit(types.JUDGING_SET_TEAM_LOCK_LOADED, { teamID: teamID, payload: response.data.result })
      }, (error) => {
        commit(types.JUDGING_SET_TEAM_LOCK_ERROR, error)
      })
    } else {
      projects.removeProjectStatus(teamID, 'Locked').then((response) => {
        commit(types.JUDGING_SET_TEAM_LOCK_LOADED, { teamID: teamID, payload: response.data.result })
      }, (error) => {
        commit(types.JUDGING_SET_TEAM_LOCK_ERROR, error)
      })
    }
  },
  fetchJudgingTeamGroups ({ commit }) {
    commit(types.JUDGING_TEAM_GROUPS_LOADING)
    judging.getJudgingTeamGroups().then((response) => {
      commit(types.JUDGING_TEAM_GROUPS_LOADED, response.data.result.groups)
    }, (error) => {
      commit(types.JUDGING_TEAM_GROUPS_ERROR, error)
    })
  },
  fetchJudgingTeam ({ commit }, { projectIDs }) {
    commit(types.JUDGING_TEAM_LOADING)
    judging.getJudgingTeams(projectIDs).then((response) => {
      commit(types.JUDGING_TEAM_LOADED, response.data.result.teams)
    }, (error) => {
      commit(types.JUDGING_TEAM_LOAD_ERROR, error)
    })
  },
  generateJudgingTeams ({ commit }, { teamSize, templateIds, teamTemplateId, filterOptions, diversifiedQuestionIds }) {
    commit(types.JUDGING_TEAM_LOADING)
    judging.generateJudgingTeams({ teamSize, templateIds, teamTemplateId, filterOptions, diversifiedQuestionIds }).then((response) => {
      commit(types.JUDGING_TEAM_LOADED, response.data.result.teams)
    }, (error) => {
      commit(types.JUDGING_TEAM_LOAD_ERROR, error)
    })
  },
  userSwitchTeam ({ commit }, { userName, fromTeam, toTeam }) {
    commit(types.JUDGING_TEAM_SWITCHING)
    judging.switchTeams({ userName, fromTeam, toTeam }).then((response) => {
      commit(types.JUDGING_TEAM_SWITCHED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_TEAM_SWITCH_ERROR, error)
    })
  },
  setTeamLead ({ commit }, { userName, teamID }) {
    commit(types.JUDGING_SET_TEAM_LEAD_PROCESSING)
    judging.setTeamLead({ userName, teamID }).then((response) => {
      commit(types.JUDGING_SET_TEAM_LEAD_PROCESSED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_SET_TEAM_LEAD_ERROR, error)
    })
  },
  removeTeamLead ({ commit }, { userName, teamID }) {
    commit(types.JUDGING_REMOVE_TEAM_LEAD_PROCESSING)
    judging.removeTeamLead({ userName, teamID }).then((response) => {
      commit(types.JUDGING_REMOVE_TEAM_LEAD_PROCESSED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_REMOVE_TEAM_LEAD_ERROR, error)
    })
  },
  removeFromTeam ({ commit }, { userName, fromTeam }) {
    commit(types.JUDGING_TEAM_USER_REMOVING)
    judging.removeUserFromTeam({ userName, teamID: fromTeam }).then((response) => {
      commit(types.JUDGING_TEAM_USER_REMOVED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_TEAM_USER_REMOVE_ERROR, error)
    })
  },
  addToTeam ({ commit }, { userName, toTeam }) {
    commit(types.JUDGING_TEAM_USER_ADDING)
    judging.addUserToTeam({ userName, teamID: toTeam }).then((response) => {
      commit(types.JUDGING_TEAM_USER_ADDED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_TEAM_USER_ADD_ERROR, error)
    })
  },
  fetchJudgingTeamProjects ({ commit }, teamID) {
    commit(types.JUDGING_TEAM_PROJECTS_LOADING, teamID)
    projects.getLinks(teamID).then((response) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOADED, { teamID: teamID, projects: response.data.result.projects })

      response.data.result.projects.forEach((p) => {
        commit(types.ADD_PROJECT, p, { root: true })
      })
    }, (error) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOAD_ERROR, error)
    })
  },
  fetchTeamProjectTags ({ commit, getters }) {
    commit(types.JUDGING_TEAM_PROJECTS_TAGS_LOADING)
    const config = getters.getJudgingConfig
    if (!config.teamProjectTemplates) {
      commit(types.JUDGING_TEAM_PROJECTS_TAGS_ERROR, 'Team project template IDs not initialized.')
    }

    users.getFilters().then((response) => {
      const filters = response.data?.result?.filters
      if (filters && filters.some(m => m.name === config.filterName)) {
        const tagGroups = filters.find(m => m.name === config.filterName)?.filterGroups
        if (!tagGroups) {
          commit(types.JUDGING_TEAM_PROJECTS_TAGS_ERROR, 'Team project filter group does not exist.')
        } else {
          commit(types.JUDGING_TEAM_PROJECTS_TAGS_LOADED, tagGroups)
        }
      } else {
        commit(types.JUDGING_TEAM_PROJECTS_TAGS_ERROR, 'Team project filter not exist.')
      }
    }, (error) => {
      commit(types.JUDGING_TEAM_PROJECTS_TAGS_ERROR, error)
    })
  },
  assignProjectsToTeam ({ commit, getters }, { teamID, tagGroups, limit, excludeLinkedProjects, replaceAll }) {
    commit(types.JUDGING_TEAM_PROJECTS_LOADING, teamID)
    const config = getters.getJudgingConfig
    if (!config.teamProjectTemplates) {
      commit(types.JUDGING_TEAM_PROJECTS_LOAD_ERROR, 'Team project template IDs not initialized.')
    }
    projects.linkProjectsByTag(teamID, tagGroups, config.teamProjectTemplates, limit, excludeLinkedProjects, replaceAll).then((response) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOADED, { teamID: response.data.result.projectID, projects: response.data.result.projects, isReplaceAll: replaceAll })
      response.data.result.projects.forEach((p) => {
        commit(types.ADD_PROJECT, p, { root: true })
      })
    }, (error) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOAD_ERROR, error)
    })
  },
  removeAllProjectsFromTeam ({ commit }, { teamID }) {
    commit(types.JUDGING_TEAM_PROJECTS_LOADING, teamID)
    projects.unlinkAll(teamID).then((response) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOADED, { teamID: response.data.result.projectID, projects: [] })
    }, (error) => {
      commit(types.JUDGING_TEAM_PROJECTS_LOAD_ERROR, error)
    })
  },
  resetJudgingTeam ({ commit }) {
    commit(types.JUDGING_TEAM_RESET)
  },
  resetJudgePairing ({ commit }) {
    commit(types.RESET_JUDGE_PAIRING)
  },
  scheduleJudgingTeamProjects ({ commit }, { teamID, startDatetime, duration }) {
    commit(types.JUDGING_SCHEDULE_TEAM_PROCESSING)
    judging.scheduleJudgingMeetings({ teamID, startDatetime, duration }).then((response) => {
      commit(types.JUDGING_SCHEDULE_TEAM_PROCESSED)
    }, (error) => {
      commit(types.JUDGING_SCHEDULE_TEAM_ERROR, error)
    })
  },
  setTeamScheduleError: ({ commit }, errorMessage) => {
    commit(types.JUDGING_SCHEDULE_TEAM_ERROR, errorMessage)
  },
  resetTeamScheduleError: ({ commit }) => {
    commit(types.JUDGING_SCHEDULE_TEAM_IDLE)
  },
  getJudgingGroupsForProject: ({ commit }, projectID) => {
    return new Promise((resolve, reject) => {
      judging.getJudgingGroupsForProject(projectID).then((response) => {
        const users1 = response.data.result.users1
        const users2 = response.data.result.users2

        const groups = response.data.result.groups
        groups[0].users = users1
        groups[1].users = users2
        resolve(groups)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateUserJudgeGroup: ({ commit }, { topic, userName, projectID }) => {
    return new Promise((resolve, reject) => {
      judging.updateUserJudgeGroup(topic, userName, projectID).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  downloadJudgingRubric ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      commit(types.JUDGING_RUBRIC_DOWNLOAD_STATUS, 'downloading')
      judging.downloadJudgingRubric(projectID).then((response) => {
        commit(types.JUDGING_RUBRIC_DOWNLOAD_STATUS, 'idle')
        if (state.judgePairingStatus !== 'UPLOADED') {
          commit(types.JUDGE_PAIRING_STATUS, 'DOWNLOADED')
        }
        resolve(response)
      }, (error) => {
        commit(types.JUDGING_RUBRIC_DOWNLOAD_ERROR, error)
        reject(error)
      })
    })
  },
  uploadJudgingRubric ({ commit }, { projectID, rubricFile }) {
    return new Promise((resolve, reject) => {
      commit(types.JUDGING_RUBRIC_UPLOAD_STATUS, 'uploading')
      judging.uploadJudgingRubric(projectID, rubricFile).then((response) => {
        commit(types.JUDGING_RUBRIC_UPLOAD_STATUS, 'idle')
        commit(types.JUDGE_PAIRING_STATUS, 'UPLOADED')
        resolve(response)
      }, (error) => {
        commit(types.JUDGING_RUBRIC_UPLOAD_ERROR, error)
        commit(types.JUDGE_PAIRING_STATUS, 'DOWNLOADED')
        reject(error)
      })
    })
  },
  getJudgePairings ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      judging.getJudgingRubric(projectID).then((response) => {
        if (response.data.result.pairing) {
          const judgePairingStatus = response.data.result.pairing.rubricStatus
          const judgePairingScore = response.data.result.pairing.rubricTotalScore
          commit(types.JUDGE_PAIRING_STATUS, judgePairingStatus)
          commit(types.JUDGE_PAIRING_SCORE, judgePairingScore)
        }
        resolve(response)
      }, (error) => {
        commit(types.JUDGE_PAIRING_ERROR, error)
        reject(error)
      })
    })
  },
  submitRubricScore ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      judging.submitRubricScore(projectID).then((response) => {
        commit(types.JUDGE_PAIRING_STATUS, 'SUBMITTED')
        resolve(response)
      }, (error) => {
        commit(types.JUDGE_PAIRING_ERROR, error)
        reject(error)
      })
    })
  }
}

const mutations = {
  [types.JUDGING_TAB_SWITCHED] (state, value) {
    state.currentTab = value
  },
  [types.JUDGING_TEAM_GROUPS_LOADING] (state) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_GROUPS_LOADED] (state, payload) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusLoaded
    state.teamGroups = payload.reduce((result, current) => {
      result[current.id] = current
      return result
    }, {})
  },
  [types.JUDGING_TEAM_GROUPS_ERROR] (state) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_TEAM_LOADING] (state) {
    state.teamsLoadingStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_LOADED] (state, payload) {
    state.teams = payload.reduce((result, current) => {
      result[current.id] = current
      return result
    }, {})
    state.teamsLoadingStatus = fetchStatuses.FetchStatusLoaded
  },
  [types.JUDGING_TEAM_LOAD_ERROR] (state, error) {
    state.teamsLoadingError = error
    state.teamsLoadingStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_TEAM_SWITCHING] (state) {
    state.teamsSwitchStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_SWITCHED] (state, { fromTeam, toTeam }) {
    Vue.set(state.teams, fromTeam.id, fromTeam)
    Vue.set(state.teams, toTeam.id, toTeam)
    state.teamsSwitchStatus = fetchStatuses.FetchStatusLoaded
  },
  [types.JUDGING_TEAM_SWITCH_ERROR] (state, error) {
    state.teamsSwitchError = error
    state.teamsSwitchStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_SET_TEAM_LEAD_PROCESSING] (state) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_SET_TEAM_LEAD_PROCESSED] (state, payload) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_ERROR] (state, error) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusError
    state.teamLeadSetError = error
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_PROCESSING] (state) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_PROCESSED] (state, payload) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_ERROR] (state, error) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusError
    state.teamLeadSetError = error
  },
  [types.JUDGING_TEAM_USER_REMOVING] (state, error) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_USER_REMOVED] (state, payload) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_TEAM_USER_REMOVE_ERROR] (state, error) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusError
    state.teamUserRemoveError = error
  },
  [types.JUDGING_TEAM_USER_ADDING] (state, error) {
    state.teamUserAddStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_USER_ADDED] (state, payload) {
    state.teamUserAddStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_TEAM_USER_ADD_ERROR] (state, error) {
    state.teamUserAddStatus = fetchStatuses.FetchStatusError
    state.teamUserAddError = error
  },
  [types.JUDGING_SET_TEAM_LOCK_LOADING] (state) {
    state.teamLockStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_SET_TEAM_LOCK_LOADED] (state, { teamID, payload }) {
    state.teamLockStatus = fetchStatuses.FetchStatusLoaded
    if (state.teams[teamID]) {
      const team = state.teams[teamID]
      if (team.projectStatus) {
        team.projectStatus.status = payload.status
      }
      Vue.set(state.teams, payload.projectID, team)
    }
  },
  [types.JUDGING_SET_TEAM_LOCK_ERROR] (state, error) {
    state.teamLockError = error
  },
  [types.JUDGING_TEAM_PROJECTS_LOADING] (state, teamID) {
    Vue.set(state.teamProjectsStatuses, teamID, fetchStatuses.FetchStatusLoading)
  },
  [types.JUDGING_TEAM_PROJECTS_LOADED] (state, { teamID, projects, isReplaceAll = true }) {
    Vue.set(state.teamProjectsStatuses, teamID, fetchStatuses.FetchStatusLoaded)
    if (isReplaceAll) {
      Vue.set(state.teamProjects, teamID, projects)
    } else {
      if (state.teamProjects[teamID]) {
        Vue.set(state.teamProjects, teamID, state.teamProjects[teamID].concat(projects))
      } else {
        Vue.set(state.teamProjects, teamID, projects)
      }
    }
  },
  [types.JUDGING_TEAM_PROJECTS_LOAD_ERROR] (state, { teamID, error }) {
    Vue.set(state.teamProjectsStatuses, teamID, fetchStatuses.FetchStatusError)
    Vue.set(state.teamProjectsErrors, teamID, error)
  },
  [types.JUDGING_TEAM_PROJECTS_TAGS_LOADING] (state) {
    state.teamProjectsTagsStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_PROJECTS_TAGS_LOADED] (state, tags) {
    state.teamProjectsTagsStatus = fetchStatuses.FetchStatusLoaded
    state.teamProjectsTags = tags
  },
  [types.JUDGING_TEAM_PROJECTS_TAGS_ERROR] (state, error) {
    state.teamProjectsTagsStatus = fetchStatuses.FetchStatusError
    state.teamProjectsTagsError = error
  },
  [types.JUDGING_TEAM_RESET] (state) {
    Object.assign(state, getDefaultState())
  },
  [types.JUDGING_SCHEDULE_TEAM_PROCESSING] (state) {
    state.teamProjectsScheduleStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_SCHEDULE_TEAM_PROCESSED] (state) {
    state.teamProjectsScheduleStatus = fetchStatuses.FetchStatusLoaded
  },
  [types.JUDGING_SCHEDULE_TEAM_ERROR] (state, error) {
    state.teamProjectsScheduleStatus = fetchStatuses.FetchStatusError
    state.teamProjectsScheduleError = error
  },
  [types.JUDGING_SCHEDULE_TEAM_IDLE] (state) {
    state.teamProjectsScheduleStatus = fetchStatuses.FetchStatusIdle
    state.teamProjectsScheduleError = null
  },
  [types.JUDGING_RUBRIC_DOWNLOAD_STATUS] (state, value) {
    state.rubricDownloadStatus = value
  },
  [types.JUDGING_RUBRIC_DOWNLOAD_ERROR] (state, value) {
    state.rubricDownloadError = value
  },
  [types.JUDGING_RUBRIC_UPLOAD_STATUS] (state, value) {
    state.rubricUploadStatus = value
  },
  [types.JUDGING_RUBRIC_UPLOAD_ERROR] (state, value) {
    state.rubricUploadError = value
  },
  [types.JUDGE_PAIRING_ERROR] (state, value) {
    state.judgePairingError = value
  },
  [types.JUDGE_PAIRING_STATUS] (state, value) {
    state.judgePairingStatus = value
  },
  [types.JUDGE_PAIRING_SCORE] (state, value) {
    state.judgePairingScore = value
  },
  [types.RESET_JUDGE_PAIRING] (state) {
    state.judgePairingStatus = null
    state.judgePairingScore = null
    state.rubricUploadError = null
    state.judgePairingError = null
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
